<template>
  <div class="checkout-container">
    <!-- Título -->
    <h2 class="checkout-title">{{ $t('checkout.title') }}</h2>

    <!-- Indicador de Progresso -->
    <div class="checkout-steps">
      <div v-for="(step, index) in steps" :key="index"
        :class="['step', { active: currentStep === index, completed: index < currentStep }]">
        <div class="step-circle">
          <span v-if="index < currentStep">✔</span>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span class="step-title">{{ step.title }}</span>
      </div>
    </div>

    <!-- Carregando Estado -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading">
        <i class="fas fa-spinner fa-spin"></i> {{ $t('loading') }}
      </div>
    </div>

    <!-- Conteúdo -->
    <div v-else class="checkout-content">
      <div v-if="currentStep === 0">
        <h3 class="step-header">{{ $t('checkout.step1.title') }}</h3>

        <div v-if="isEmpty" class="empty-cart">
          <p>{{ $t('checkout.step1.empty_cart') }}</p>
          <router-link to="/" class="btn-back-to-shopping">
            {{ $t('checkout.step1.back_to_shopping') }}
          </router-link>
        </div>

        <div v-else>
          <div v-for="(item, index) in filteredCartItems" :key="index" class="cart-item">
            <img :src="getProductImage(item)" alt="Produto" class="item-image" />
            <div class="item-details">
              <h>{{ item.produto.codigo }}</h>
              <h4>
                {{ item.produto.titulo === item.produto.nome ? item.produto.titulo : item.produto.titulo + ' ' +
                  item.produto.nome }}
              </h4>

              <div class="item-actions">
                <button @click="updateQuantity(item, item.qtde - 1)" class="quantity-button">-</button>
                <span class="item-quantity">{{ Math.floor(item.qtde) }}</span>
                <button @click="updateQuantity(item, item.qtde + 1)" class="quantity-button">+</button>
              </div>
            </div>
            <div class="item-actions-container">
              <button @click="removeItem(item)" class="btn-remove" :disabled="removingItem === item.produto_id">
                <i v-if="removingItem === item.produto_id" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-trash-alt"></i>
              </button>

              <p class="item-price">{{ formatPrice(item.valor_unitario) }}</p>
            </div>
          </div>



          <div class="total-price">
            <strong class="total-value">{{ formatPrice(totalPrice) }}</strong>
            <span class="total-label">
              {{ $store.state.ecommerce.cart.moeda?.id === 3
                ? $t('checkout.step1.totalIva')
                : $t('checkout.step1.total') }}
            </span>
          </div>

          <!-- Componente de Cálculo de Frete -->
          <ShippingCalculator v-if="isAuthenticated && filteredCartItems.length > 0" :cart="$store.state.ecommerce.cart"
            :onShippingCalculated="updateShippingCost" @shipping-selected="handleShippingSelected" />
          <div class="shipping">
            <strong class="total-label">{{ $t('checkout.step1.shipping') }}:</strong>
            <span class="total-value">{{ formatPrice(shippingCost) }}</span>
          </div>
          <div class="total-with-shipping">
            <strong class="total-label">{{ $t('checkout.step1.total_with_shipping') }}:</strong>
            <span class="total-value">{{ formatPrice(calculateTotalPriceWithShipping()) }}</span>
          </div>
          <div class="checkout-actions">
            <button v-if="isAuthenticated" @click="nextStep" class="btn-primary">
              {{ $t('checkout.step1.continue') }}
            </button>
            <div v-show="!isAuthenticated" class="login-warning">
              <p class="login-message">
                {{ $t('checkout.step1.login_message') }}
              </p>
              <div class="login-actions">
                <LoginView @loggedin="loadPage()" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Etapa 2: Pagamento -->
      <div v-if="currentStep === 1">
        <div class="actions">
          <button @click="prevStep" class="btn-secondary">
            {{ $t('checkout.step2.back') }}
          </button>
          <button @click="nextStep" class="btn-primary" :disabled="!paymentMethod">
            {{ $t('checkout.step2.go_to_payment') }}
          </button>
        </div>
      </div>

      <!-- Exibindo erro se houver -->
      <div v-if="paymentErrorMessage" class="error-message">
        <p>{{ paymentErrorMessage }}</p>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";
import defaultImage from '@/assets/images/produto-sem-imagem.png';
import ShippingCalculator from './components/ShippingCalculator.vue';
import LoginView from "./Usuario/LoginView.vue";
import { resolveError } from "@/components/helpers/responseHelper";

export default {
  components: {
    ShippingCalculator,
    LoginView,
  },

  data() {
    return {
      currentStep: 0,
      paymentMethod: null,
      paymentError: null,
      paymentExternalId: localStorage.getItem('paymentExternalId'),
      isLoading: false,
      removingItem: null,
      shippingCost: 0,
      steps: [
        { title: this.$t('checkout.step1.title') },
        { title: this.$t('checkout.step2.payment') },
      ],
      token_entrega: '',
      paymentErrorMessage: null,
      paymentError: false,
    };
  },

  computed: {
    isAuthenticated() {
      return !!this.$store.state.auth.user;
    },
    isEmpty() {
      return this.filteredCartItems.length === 0;
    },
    filteredCartItems() {
      return (
        this.$store.state.ecommerce.cart.itens || []
      ).filter((item) => item.ativo !== false);
    },
    totalPrice() {
      const moeda = this.$store.state.ecommerce.cart.moeda;
      const total = Number(this.$store.state.ecommerce.cart.valor_total) || 0;
      const currencySymbol = moeda?.simbolo || "R$";

      return this.calculateTotalPrice();
    },
    isPortuguese() {
      return this.$i18n.locale === 'pt-BR';
    },
  },

  mounted() {
    this.loadPage()
  },

  methods: {
    loadPage() {
      this.loadCart();

    },
    async waitForAuth() {
      return new Promise((resolve) => {
        const checkAuth = () => {
          if (this.$store.state.auth.user) {
            resolve();
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    },
    calculateSubtotal() {
      return this.filteredCartItems.reduce(
        (total, item) => total + (parseFloat(item.valor_unitario) || 0) * Math.floor(item.qtde),
        0
      );
    },
    handleShippingSelected(shippingData) {
      this.shippingCost = shippingData.valor;

      // Padronize como o token é obtido
      this.token_entrega = shippingData.selectedOption?.token ||
        shippingData.ponto_entrega?.token_servico;

      // Armazene todos os dados relevantes no estado/localStorage
      this.selectedShippingMethod = shippingData.tipo;
      this.selectedShippingOption = shippingData.selectedOption || shippingData.ponto_entrega;
    },
    async loadCart() {
      try {
        this.isLoading = true;
        await this.$store.dispatch('ecommerce/getCart');


      } catch (error) {
        console.error("Erro ao carregar carrinho:", error);
        alert(this.$t('checkout.error_request'));
      } finally {
        this.isLoading = false;
      }
    },
    getProductImage(item) {
      return item.produto.imagens?.[0]?.url || defaultImage;
    },
    async nextStep() {
      this.paymentError = false;
      this.paymentErrorMessage = null;

      // 1. Validações iniciais
      if (!this.isAuthenticated) {
        this.currentStep = 0;
        alert(this.$t('checkout.step1.login_message'));
        return;
      }

      if (this.isEmpty) {
        alert(this.$t('checkout.step1.empty_cart'));
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        alert(this.$t('checkout.step1.login_message'));
        return;
      }

      // 2. Obter dados de frete
      const shippingData = JSON.parse(localStorage.getItem('shippingData') || '{}');
      const externalId = this.$store.state.ecommerce.external_id;

      // 3. Validação reforçada do método de entrega
      if (!this.token_entrega) {
        alert(this.$t('checkout.step1.select_shipping'));
        return;
      }

      // Validação específica para retirada
      if (shippingData.selectedDeliveryType === 'pickup_point' && !shippingData.pickupPoint) {
        alert(this.$t('checkout.step1.select_pickup_point'));
        return;
      }

      // 4. Montagem do payload dinâmico
      const payload = {
        token_entrega: this.token_entrega,
        tipo_entrega: shippingData.selectedDeliveryType === 'pickup_point' ? 'retirada' : 'entrega',
        valor_frete: this.shippingCost,
        cliente_endereco_id: shippingData.cliente_endereco_id
      };


      // Adiciona dados específicos para retirada
      if (shippingData.selectedDeliveryType === 'pickup_point' && shippingData.pickupPoint) {
        payload.cidade_alternativa = shippingData.pickupPoint.cidade_id;
        payload.id_ponto_entrega = shippingData.pickupPoint.id;
      }

      // 5. Configuração da requisição
      const url = `${process.env.VUE_APP_API_URI}/ecom/venda/carrinho/${externalId}/iniciar-checkout`;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // 6. Execução da requisição
      this.isLoading = true;

      try {
        const response = await axios.post(url, payload, { headers });

        if (!response.data?.link_pagamento?.length) {
          throw new Error('Link de pagamento não encontrado');
        }

        const [paymentLink] = response.data.link_pagamento;
        localStorage.setItem('paymentExternalId', externalId);

        if (paymentLink) {
          await this.$store.dispatch("ecommerce/clearCart");
          window.location.href = paymentLink;
        } else {
          this.paymentError = true;
          this.paymentErrorMessage = this.$t('checkout.error.payment_link');
        }

      } catch (error) {
        console.error("Erro no checkout:", error);
        this.paymentError = true;

        this.paymentErrorMessage = resolveError(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleLoginSuccess() {
      this.$store.dispatch("auth/checkAuth").then(() => {
        this.currentStep = 1;
      });
    },

    prevStep() {
      if (this.currentStep > 0) this.currentStep--;
    },

    removeItem(item) {
      this.removingItem = item.produto_id;

      this.$store.dispatch("ecommerce/removeFromCart", item).then(() => {
        return this.$store.dispatch('ecommerce/getCart');
      }).then(() => {
        this.removingItem = null;
      }).catch(() => {
        this.removingItem = null;
      });
    },

    updateQuantity(item, newQuantity) {
      const quantity = Math.max(1, Math.floor(newQuantity));
      if (quantity !== item.qtde) {
        this.$store.dispatch('ecommerce/updateProductQuantity', {
          produto_id: item.produto_id,
          qtde: quantity,
        }).then(() => {
          this.loadCart();
        });

      }
    },

    formatPrice(price) {
      const moeda = this.$store.state.ecommerce.cart.moeda;
      const currencySymbol = moeda?.simbolo || "R$";

      if (currencySymbol === "U$") {
        return `U$ ${parseFloat(price).toFixed(2)}`;
      } else if (currencySymbol === "G$") {
        return `G$ ${parseFloat(price).toLocaleString('pt-BR')}`;
      } else {
        return `${currencySymbol} ${parseFloat(price).toFixed(2)}`;
      }
    },

    updateShippingCost(cost) {
      this.shippingCost = cost;
    },
    selectedDeliveryOption(option) {

      this.token_entrega = option.token
    },
    calculateTotalPrice() {
      const subtotal = this.filteredCartItems.reduce(
        (total, item) => total + (parseFloat(item.valor_unitario) || 0) * Math.floor(item.qtde),
        0
      );
      return subtotal
    },

    calculateTotalPriceWithShipping() {
      const subtotal = this.filteredCartItems.reduce((total, item) => total + (parseFloat(item.valor_unitario) || 0) * Math.floor(item.qtde), 0);
      return subtotal + this.shippingCost;
    },
  },
  watch: {
    isLoading(newVal) {
      if (newVal) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth' // Rolagem suave (opcional)
        });
      }
    }
  },
};

</script>

<style scoped>
.checkout-container {
  max-width: 900px;
  margin: 20px auto;

  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  min-height: 80vh;
  text-align: center;
}



.register-now {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}



.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.step-header {
  text-align: left;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.loading {
  color: #3498db;
  font-size: 1.5rem;
  text-align: center;
}

.btn-login {
  padding: 15px 30px;
  font-size: 18px;
  color: white;
  border-radius: 5px;
  text-decoration: none;
  background-color: #E23B33;
  transition: background-color 0.3s;
  min-width: 200px;
  text-align: center;
  margin-bottom: 10px;
}

.btn-login:hover {
  background-color: #b58c4f;
}

.subtotal,
.shipping,
.total-with-shipping {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.total-with-shipping {
  font-size: 1.2em;
  font-weight: bold;
  border-top: 1px solid #ddd;
  padding-top: 10px;
}

.login-warning {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  margin-top: 0;
  top: 50%;
  left: 50%;
  width: 100%;
}


.login-message {
  font-size: 16px;
  color: #E23B33;
  margin-bottom: 15px;
  text-align: center;
}


.error-message {
  margin-top: 15px;
  color: red;
  font-size: 14px;
}

.register-message {
  font-size: 14px;
  color: #333;
  margin-top: 15px;
}

.btn-register {
  font-size: 16px;
  color: #007bff;
  text-decoration: none;
  margin-top: 10px;
  text-align: center;
}

.btn-register:hover {
  color: #0056b3;
}

.checkout-title {
  font-size: 22px;
  color: #ffffff;
  background-color: #E23B33;
  display: inline-block;
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 50px;
  padding-right: 50px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  margin: 0 auto;
  box-shadow: none;
  border: none;
  text-align: center;
  text-decoration: none;
  margin-bottom: 30px;
}


.checkout-steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.step {
  text-align: center;
  flex: 1;
  position: relative;
  color: #bbb;
  transition: color 0.3s;
}

.shipping {
  margin-top: 40px;
}


.item-quantity {
  font-size: 16px;
  margin: 0 10px;
  color: #333;
}


.item-price {
  color: #E23B33;
  font-size: 1.2em;
  margin-top: 30px;
}

.step.active {
  color: #E23B33;
}

.step.completed {
  color: #E23B33;
}

.step-circle {
  width: 40px;
  height: 40px;
  line-height: 40px;
  margin: 0 auto;
  border-radius: 50%;
  background: #bbb;
  color: white;
  font-size: 18px;
  font-weight: bold;
}

.step.active .step-circle,
.step.completed .step-circle {
  background: #E23B33;
}

.step-title {
  margin-top: 10px;
  font-size: 14px;
}

.step::before {
  content: "";
  position: absolute;
  height: 5px;
  width: 100%;
  background: #bbb;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: -1;
}

.step:first-child::before {
  width: 0;
}

.step.active::before,
.step.completed::before {
  background: #E23B33;
}

.checkout-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}



.item-image {
  width: 80px;
  height: 80px;
  margin-right: 15px;
  border-radius: 8px;
}

.item-details {
  flex: 1;

}



.item-details h4 {
  margin: 0;
  font-size: 18px;
  margin-bottom: 20px;
}

.cart-item {
  display: flex;
  position: relative;
  text-align: left;
  padding: 15px;
  border-bottom: 1px solid #eee;
  font-size: 16px;
  color: #333;
}

.btn-remove {
  background-color: transparent;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.btn-remove i,
.btn-remove svg {
  font-size: 18px;
  color: rgb(255, 95, 95);
}

.btn-remove:hover i,
.btn-remove:hover svg {
  transform: scale(1.2);
  color: #ff0000;
}


.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.btn-primary {
  background: #E23B33;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-primary:hover {
  background: #E23B33;
}

.btn-primary:disabled {
  background: #b0c4de;
}

.btn-secondary {
  background: #757575;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-secondary:hover {
  background: #616161;
}

.total-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 20px;
  font-size: 18px;
  color: #333;
}

.total-value {
  font-size: 24px;
  color: #E23B33;
}

.total-label {
  font-size: 14px;
  color: #E23B33;
  margin-top: 10px;
}


.item-actions {
  display: flex;
  align-items: center;
}

.item-actions-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}

.checkout-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.quantity-button {
  background-color: #E23B33;
  color: white;
  border: 1px solid #E23B33;
  border-radius: 5px;
  padding: 8px 12px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.quantity-button:hover {
  background-color: #9e2c2c;
}

.btn-login {
  background-color: #E23B33;
  color: white;
  padding: 10px 20px;
  text-decoration: none;
  border-radius: 5px;
  min-width: 200px;
  text-align: center;
  font-size: 18px;
}

.btn-login:hover {
  background-color: #8f2525;
}

.item-actions button {
  padding: 6px 12px;
  border: none;
  background-color: #E23B33;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  margin: 0 5px;
}

.item-actions button:hover {
  background-color: #8f2525;
}
</style>
